import type { GraphLayout } from '@yuan-math/curriculum';

/**
 * Turning a `GraphLayout` into coordinates.
 *
 * All the graph reasoning — which layer a concept sits in, and in what order
 * within it — is `layoutConceptGraph`'s, in the curriculum package. This is the
 * arithmetic that follows: centre each layer over the widest one and hand back
 * a box for every node. It is the only part the Math Map and the kingdom skill
 * tree share, and they share it because drawing the same graph two subtly
 * different ways would be the bug nobody notices.
 *
 * The sizes are the caller's, because the two drawings are not the same size: a
 * kingdom holds tens of concepts and can afford smaller boxes than a map of all
 * 285.
 */

export type NodeBox = { x: number; y: number };

export type GraphPositions = {
  /** Top-left corner of every node the layout placed. */
  map: Map<string, NodeBox>;
  /** The drawing's own size, before any zoom. */
  width: number;
  height: number;
};

export type GraphMetrics = {
  nodeWidth: number;
  nodeHeight: number;
  columnGap: number;
  rowGap: number;
  /** Room above the first layer, so a node's top edge is not the canvas edge. */
  padTop?: number;
  padBottom?: number;
};

export function graphPositions(layout: GraphLayout, metrics: GraphMetrics): GraphPositions {
  const { nodeWidth, nodeHeight, columnGap, rowGap, padTop = 40, padBottom = 20 } = metrics;

  const map = new Map<string, NodeBox>();
  const widest = Math.max(...layout.layers.map((layer) => layer.length), 1);
  const width = widest * (nodeWidth + columnGap);

  for (const node of layout.nodes) {
    const layer = layout.layers[node.layer] ?? [];
    const rowWidth = layer.length * (nodeWidth + columnGap);
    const offset = (width - rowWidth) / 2;
    map.set(node.id, {
      x: offset + node.order * (nodeWidth + columnGap) + columnGap / 2,
      y: node.layer * (nodeHeight + rowGap) + padTop,
    });
  }

  return { map, width, height: layout.layers.length * (nodeHeight + rowGap) + padTop + padBottom };
}
